import l from "components/util/l";

// A table diff compares every row, so a large collection would render tens of thousands of
// lines. The counts stay real because they come from the payload, not from what is shown.
const ROW_LIMIT = 200;

const FLOW_FIELD_LABELS = {
  name: l("Nombre%GA074"),
  description: l("Descripción%GA077"),
  initial_prompt: l("Prompt inicial%VG280"),
  chat_settings: l("Ajustes de chat%VG272"),
  presentation_settings: l("Ajustes de presentación%VG274"),
  security_settings: l("Ajustes de seguridad%VG276"),
  permissions: l("Permisos%PA107"),
  token: l("Token de solución%VG260"),
};

const COLLECTION_FIELD_LABELS = {
  label: l("Nombre%GA074"),
  type: l("Tipo%GA079"),
  is_active: l("Activa%VG337"),
};

const COLUMN_FIELD_LABELS = {
  label: l("Nombre%GA074"),
  type: l("Tipo%GA079"),
};

const EXTRA_LABELS = {
  chats: l("Chats%VG194"),
  revisions: l("Revisiones%VG302"),
  flow_chat_settings: l("Ajustes de chat%VG272"),
  flow_presentation_views: l("Vistas personalizadas%VG292"),
  flow_presentation_theme: l("Tema visual%VG294"),
  flow_security_settings: l("Ajustes de seguridad%VG276"),
  flow_permissions: l("Permisos%PA107"),
  participant_roles: l("Roles%VG359"),
  licenses: l("Licencias%PA895"),
  flow_token: l("Token de solución%VG260"),
};

const GROUP_LABELS = {
  flow: l("Datos generales%VG343"),
  processes: l("Procesos%PA114"),
  collections: l("Tablas%VG195"),
  extras: l("Otros%DF001"),
};

const emptyCounts = () => ({ added: 0, modified: 0, removed: 0, unchanged: 0 });

const addCounts = (first, second) => ({
  added: first.added + (second?.added || 0),
  modified: first.modified + (second?.modified || 0),
  removed: first.removed + (second?.removed || 0),
  unchanged: first.unchanged + (second?.unchanged || 0),
});

const countOf = (status) => ({ ...emptyCounts(), [status]: 1 });

const isChanged = (counts) =>
  counts.added > 0 || counts.modified > 0 || counts.removed > 0;

/* -------------------------------------------------------------------------- */
/* Fields                                                                      */
/* -------------------------------------------------------------------------- */

const buildFields = (fields, labels = {}) =>
  (fields || []).map((field) => ({
    key: field.field,
    label: field.label || labels[field.field] || field.field,
    current: field.current,
    imported: field.imported,
  }));

/* -------------------------------------------------------------------------- */
/* Processes                                                                   */
/* -------------------------------------------------------------------------- */

// The node's own status plus everything below it, so the tree entry can show what the
// collapsed branch is hiding. The key is a path because a node key only identifies the node
// inside its branch, and repeated keys would share collapse state across the whole tree.
const buildNode = (node, index, parentKey = "") => {
  const key = `${parentKey}/${index}-${node.key}`;
  const children = (node.children || []).map((child, childIndex) => buildNode(child, childIndex, key));
  const counts = children.reduce((acc, child) => addCounts(acc, child.counts), countOf(node.status));

  return {
    key,
    label: node.label,
    sublabel: node.action == node.label ? "" : node.action || "",
    status: node.status,
    fields: buildFields(node.fields),
    blocks: [],
    children,
    counts,
  };
};

/* -------------------------------------------------------------------------- */
/* Collections                                                                 */
/* -------------------------------------------------------------------------- */

const buildDiffItems = (diff, status, labels) =>
  (diff[status] || []).map((item) => ({
    key: item.key,
    label: item.label,
    status,
    fields: buildFields(item.fields, labels),
  }));

const buildDiffBlock = (diff, key, label, labels) => {
  const items = [
    ...buildDiffItems(diff, "added", labels),
    ...buildDiffItems(diff, "modified", labels),
    ...buildDiffItems(diff, "removed", labels),
  ];

  return {
    key,
    label,
    counts: { ...emptyCounts(), ...(diff.summary || {}) },
    items: items.slice(0, ROW_LIMIT),
    hiddenCount: Math.max(items.length - ROW_LIMIT, 0),
  };
};

const buildCollection = (collection) => {
  const blocks = [];

  if (collection.structure_enabled)
    blocks.push(buildDiffBlock(collection.structure, "structure", l("Estructura%VG335"), COLUMN_FIELD_LABELS));

  if (collection.data_enabled)
    blocks.push(buildDiffBlock(collection.data, "data", l("Registros%CA067"), {}));

  const fields = buildFields(collection.collection?.fields, COLLECTION_FIELD_LABELS);
  const counts = addCounts(
    blocks.reduce((acc, block) => addCounts(acc, block.counts), emptyCounts()),
    { modified: fields.length },
  );

  // get_pair_status only reports presence, so a table that did not change still arrives as
  // "modified". Anything that is in both sides and moved nothing is unchanged.
  const status = collection.status == "modified" && !isChanged(counts) ? "unchanged" : collection.status;

  return {
    key: collection.name,
    label: collection.name,
    sublabel: l("Tabla%GA054"),
    status,
    fields,
    blocks: blocks.filter((block) => isChanged(block.counts)),
    children: [],
    counts,
  };
};

/* -------------------------------------------------------------------------- */
/* Flow and extras                                                             */
/* -------------------------------------------------------------------------- */

const buildFlowEntity = (flow) => {
  const fields = buildFields(flow.fields, FLOW_FIELD_LABELS);

  return {
    key: "flow",
    label: GROUP_LABELS.flow,
    sublabel: "",
    status: flow.mode == "create" ? "added" : "modified",
    fields,
    blocks: [],
    children: [],
    counts: { ...emptyCounts(), modified: fields.length },
  };
};

const buildExtraEntity = (key, extra) => ({
  key,
  label: EXTRA_LABELS[key] || key,
  sublabel: extra.current == null ? "" :
    `${l("Actual%PA820")}: ${extra.current} · ${l("Importado%PA821")}: ${extra.imported}`,
  status: "modified",
  fields: [],
  blocks: [],
  children: [],
  counts: { ...emptyCounts(), ...(extra.summary || {}) },
});

/* -------------------------------------------------------------------------- */
/* Entry point                                                                 */
/* -------------------------------------------------------------------------- */

const buildGroup = (key, entities) => ({
  key,
  label: GROUP_LABELS[key],
  entities,
  counts: entities.reduce((acc, entity) => addCounts(acc, entity.counts), emptyCounts()),
});

export const buildChangeViewModel = (preview) => {
  if (!preview) return { groups: [], counts: emptyCounts(), isEmpty: true };

  const groups = [];

  if (preview.flow?.has_changes)
    groups.push(buildGroup("flow", [buildFlowEntity(preview.flow)]));

  if (preview.processes?.enabled)
    groups.push(buildGroup("processes", (preview.processes.items || []).map((node, index) => buildNode(node, index))));

  if (preview.collections?.enabled)
    groups.push(buildGroup("collections", (preview.collections.items || []).map(buildCollection)));

  // build_option_preview reports a change whenever its option is on, so an enabled toggle
  // would show up as a change on every run. The settings it covers are already diffed field
  // by field in the flow group; only the entries carrying a real count add anything.
  const extras = Object.entries(preview.extras || {})
    .filter(([, extra]) => extra.has_changes && extra.current != null)
    .map(([key, extra]) => buildExtraEntity(key, extra));

  if (extras.length > 0) groups.push(buildGroup("extras", extras));

  const withEntities = groups.filter((group) => group.entities.length > 0);
  const counts = withEntities.reduce((acc, group) => addCounts(acc, group.counts), emptyCounts());

  return {
    groups: withEntities,
    counts,
    isEmpty: !isChanged(counts),
  };
};
